import { useEffect, type ReactNode } from 'react';
import { type UseFormReturn } from 'react-hook-form';
import { slugify } from '@/shared/lib/slugify';
import { t } from '@/shared/i18n';
import { Field } from '@/shared/ui/Field';
import { Input } from '@/shared/ui/Input';
import { Textarea } from '@/shared/ui/Textarea';

import { ColorPicker } from './ColorPicker';
import type { SpaceValues } from './schema';
import styles from './SpaceForm.module.scss';

interface Props {
  form: UseFormReturn<SpaceValues>;
  /** При создании адрес подсказывается из названия и редактируется; при правке — заморожен. */
  mode: 'create' | 'edit';
  id: string;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  /** Дополнительные поля после основных — например, выбор шаблона при создании. */
  children?: ReactNode;
}

/** Поля проекта — общие для создания и настроек. */
export function SpaceForm({ form, mode, id, onSubmit, children }: Props) {
  const name = form.watch('name');

  useEffect(() => {
    if (mode === 'create' && !form.formState.dirtyFields.slug) {
      form.setValue('slug', slugify(name));
    }
  }, [name, form, mode]);

  return (
    <form id={id} className={styles['form']} onSubmit={onSubmit} noValidate>
      <Field label={t.space.name} required error={form.formState.errors.name?.message}>
        <Input
          placeholder={t.space.namePlaceholder}
          autoFocus={mode === 'create'}
          {...form.register('name')}
        />
      </Field>

      {mode === 'create' && (
        <Field
          label={t.space.slug}
          required
          hint={t.space.slugHint}
          error={form.formState.errors.slug?.message}
        >
          <Input mono {...form.register('slug')} />
        </Field>
      )}

      <Field label={t.space.description} error={form.formState.errors.description?.message}>
        <Textarea
          placeholder={t.space.descriptionPlaceholder}
          rows={2}
          {...form.register('description')}
        />
      </Field>

      <Field label={t.space.color}>
        <ColorPicker
          value={form.watch('color')}
          onChange={(color) => {
            form.setValue('color', color, { shouldDirty: true });
          }}
        />
      </Field>
      {children}
    </form>
  );
}
